import identicon from 'identicon';
import { TezosToolkit, ContractAbstraction, ContractProvider, Wallet } from '@taquito/taquito';
import { AccountInfo } from '@airgap/beacon-sdk';
import { BeaconWallet } from '@taquito/beacon-wallet';
import { TradeFromTzstats } from '@/lib/types-helper';
import { Module, VuexModule, Mutation, MutationAction, Action, getModule } from 'vuex-module-decorators';
import { TezosFA2TraderAnonAddress,
  TezosFA2TraderAnonAddressTestnet,
  TezosFA2TraderBigMapID,
  TezosFA2TraderBigMapIDTestnet } from '@/lib/constants';
import store from '@/store/store';

export interface IModule1State {
  account: AccountInfo | null | undefined;
  allContractTradeData: TradeFromTzstats[];
  myContractTradeData: TradeFromTzstats[];
  xtznftswapContract: ContractAbstraction<ContractProvider|Wallet> | null;
  signature: string;
  avatarImage: string;
  browserChain: string | number | undefined;
  tezosToolkitInstance: TezosToolkit;
  tab: number;
  transactionInFlight: boolean;
  beaconWalletInstance: BeaconWallet;
}

@Module({
  name: 'module1',
  store,
  dynamic: true
})
class Module1 extends VuexModule implements IModule1State {
  public accountAddress: string | null | undefined = localStorage.getItem('xtznftswap-account');
  public account: AccountInfo | null | undefined = null;
  public allContractTradeData: TradeFromTzstats[] = [];
  public myContractTradeData: TradeFromTzstats[] = [];
  public xtznftswapContract: ContractAbstraction<ContractProvider|Wallet> | null = null;
  public signature = ''
  public avatarImage = '';
  public browserChain: string | number | undefined = undefined
  public tezosToolkitInstance: TezosToolkit = new TezosToolkit('');
  public tab = 3;
  public transactionInFlight = false;
  public beaconWalletInstance: BeaconWallet = new BeaconWallet({
    name: 'XTZNFTSWAP'
  });

  get isDev () : boolean {
    const searchParams = new URLSearchParams(window.location.search);
    return !!(searchParams.get('dev') && process.env.NODE_ENV === 'development');
  }

  get net () : string {
    return this.isDev ? 'ghostnet' : 'mainnet';
  }

  get contractAddress () : string {
    return this.isDev ? TezosFA2TraderAnonAddressTestnet : TezosFA2TraderAnonAddress;
  }

  get bigMapAddress () : string {
    return this.isDev ? TezosFA2TraderAnonAddressTestnet : TezosFA2TraderAnonAddress;
  }

  get rpc () : string {
    return `https://${this.net}.smartpy.io`;
  }

  // @dev: can use either of the following references for state anywhere
  // * Module1Module.account; /* after importing */
  // * this.$store.state.module1.account; /* no import required */

  @Mutation
  SET_AVATAR_IMAGE (avatarImage: typeof this.avatarImage) : void {
    this.avatarImage = avatarImage;
  }

  @Mutation
  SET_ACCOUNT (account: typeof this.account) : void {
    this.account = account;

    if (account) {
      this.accountAddress = account.address;
      localStorage.setItem('xtznftswap-account', account.address);
    } else {
      this.accountAddress = null;
      localStorage.removeItem('xtznftswap-account');
    }
  }

  @Mutation
  SET_TRADE_DATA (allContractTradeData: typeof this.allContractTradeData) : void {
    this.allContractTradeData = allContractTradeData;
  }

  @Mutation
  SET_SWAP_CONTRACT (xtznftswapContract: typeof this.xtznftswapContract) : void {
    this.xtznftswapContract = xtznftswapContract;
  }

  @Mutation
  async SET_TOOLKIT (rpc: string) : Promise<void> {
    this.tezosToolkitInstance = new TezosToolkit(rpc);
    if (this.tezosToolkitInstance) {
      await this.tezosToolkitInstance.setWalletProvider(this.beaconWalletInstance);
    }
  }

  @Action
  async SetAccount () : Promise<void> {
    const account = await this.beaconWalletInstance.client.getActiveAccount();
    this.SET_ACCOUNT(account);

    // generate avatar icon
    if (this.account && this.account.address) {
      identicon.generate({
        id: this.account.address,
        size: 32
      }, (err: any, buffer: any) => {
        if (!err) {
          this.SET_AVATAR_IMAGE(buffer);
        }
      });
    }
  }

  @Action
  async UnsetAccount () : Promise<void> {
    this.SET_ACCOUNT(null);
    this.SET_AVATAR_IMAGE('');
  }

  @Action
  SetTradeData (allContractTradeData: typeof this.allContractTradeData) : void {
    this.SET_TRADE_DATA(allContractTradeData);
  }

  @Action
  SetSwapContract (xtznftswapContract: typeof this.xtznftswapContract) : void {
    this.SET_SWAP_CONTRACT(xtznftswapContract);
  }

  @Action
  async SetToolkit (rpc: string) : Promise<void> {
    await this.SET_TOOLKIT(rpc);
  }

  @Action
  async Login () : Promise<void> {
    // request login access
    await this.beaconWalletInstance.requestPermissions({
      network: {
        type: this.net as any
      }
    });

    // try ECAD
    let rpc = `https://${this.net}.api.tez.ie`;
    await this.SetToolkit(rpc);
    await this.SetAccount();

    let xtznftswapContract;
    try {
      // will die if the RPC is down
      xtznftswapContract = await this.tezosToolkitInstance.contract.at(
        this.contractAddress
      );
    } catch {
      // try smartpy
      rpc = `https://${this.net}.smartpy.io`;
      await this.SetToolkit(rpc);
      await this.SetAccount();
      // if this errors then we actually throw again
      xtznftswapContract = await this.tezosToolkitInstance.contract.at(
        this.contractAddress
      );
    }

    this.SetSwapContract(xtznftswapContract);
    await this.FetchAllContractTradeData();
  }

  @Action
  async Logout () : Promise<void> {
    await this.beaconWalletInstance.clearActiveAccount();
    await this.SetToolkit('');
    await this.UnsetAccount();
    this.SetSwapContract(null);
  }

  @MutationAction({
    mutate: ['allContractTradeData', 'myContractTradeData']
  })
  async FetchAllContractTradeData () : Promise<{ allContractTradeData: TradeFromTzstats[], myContractTradeData: TradeFromTzstats[] }> {
    try {
      let tradesBigMapID : string; // note: number as string
      if (!this.xtznftswapContract) {
        tradesBigMapID = this.isDev ? TezosFA2TraderBigMapIDTestnet : TezosFA2TraderBigMapID;
      } else {
        const storage : any = await this.xtznftswapContract.storage();
        tradesBigMapID = storage.trades.id.toNumber();
      }

      const baseURL = this.isDev ? 'https://api.ghostnet.tzkt.io' : 'https://api.tzkt.io';
      const tzstateResponse = await fetch(`${baseURL}/v1/bigmaps/${tradesBigMapID}/keys`);
      const data: any = await tzstateResponse.json();

      // just making local reference for use by the filter function
      const acc = this.account;
      let result = [];
      if (acc) {
        // only return relevant trades where they are the acceptor or proposer
        // doesn't define `this` even though arrow function
        result = data.filter((datum: TradeFromTzstats) => {
          return (
            datum.value.proposal.acceptor === acc.address ||
            datum.value.proposal.proposer === acc.address
          );
        });
      }

      return {
        allContractTradeData: data,
        myContractTradeData: result
      };
    } catch {
      return {
        allContractTradeData: [],
        myContractTradeData: []
      };
    }

    return {
      allContractTradeData: [],
      myContractTradeData: []
    };
  }
}

export const Module1Module = getModule(Module1);
